import type { SlideFields } from "@/types/Slide";
import type { Passage, SlideElement, SubQuiz } from "@ai-teach/types";
import { elementsToSubQuiz } from "@ai-teach/utils";

/** True for a standalone reading + fluency slide — the only reading knowledge area that can merge. */
export function isFluencyReadingSlide(slide: SlideFields): boolean {
  return slide.type === "reading" && slide.knowledgeArea === "fluency";
}

/**
 * A slide can HOLD subQuizzes (be a merge target/master) if it's an "other" slide or a reading-fluency
 * slide — only these carry `slideData.subQuizzes`. Linked copies are excluded: their content is synced
 * from the original and can't be edited directly.
 */
export function isSubQuizMaster(slide: SlideFields): boolean {
  if (slide.isLinkedCopy) return false;
  return slide.type === "other" || isFluencyReadingSlide(slide);
}

/**
 * A slide can BE absorbed into a master's subQuizzes if it's a standalone "other" slide (its cloze
 * question lives in `elements`) or a standalone reading-fluency slide (its `passages` and `subQuizzes`
 * fold into the master's). Linked copies are excluded (deleting one is a linked-copy operation). This
 * only says the slide is a valid source *in general* — whether a given master accepts it is decided by
 * `canAbsorbSlide`.
 *
 * An ORIGINAL that still has linked copies is excluded too. Absorbing deletes the source, and deleting
 * an original severs its copies — the API unlinks them into standalone slides in whatever lessons they
 * live in. That side effect reaches beyond the lesson being merged and the merge UI never mentions it.
 * The API refuses this delete anyway (CANNOT_DELETE_ORIGINAL_WITH_LINKED_COPIES) for everyone but a
 * superuser, and it refuses it only *after* the master has been updated — so without this check the
 * merge half-completes: the master keeps the absorbed quizzes and the sources survive as duplicates.
 *
 * `linkedCopies` resolves from the same predicate the delete guard uses (live, non-deleted copies of
 * this original), so this rejects exactly the sources the server would refuse — keep the two in step.
 * It fails OPEN if that loader errors and returns empty; the server guard is still the real stop, and
 * the caller reports the half-completed state from whether the master update committed.
 */
export function isSubQuizSource(slide: SlideFields): boolean {
  if (slide.isLinkedCopy) return false;
  if (slide.linkedCopies.length > 0) return false;
  return slide.type === "other" || isFluencyReadingSlide(slide);
}

/**
 * Pairs a candidate source against a specific master. An "other" source can be absorbed into any
 * eligible master (existing behavior, unchanged). A reading-fluency source carries `passages`, which
 * only a reading-fluency master has room for — so it can only be absorbed into a reading-fluency
 * master, never an "other" one.
 */
export function canAbsorbSlide(slide: SlideFields, masterIsFluencyReading: boolean): boolean {
  if (!isSubQuizSource(slide)) return false;
  return !isFluencyReadingSlide(slide) || masterIsFluencyReading;
}

interface AbsorbableSlideData {
  subQuizzes?: SubQuiz[];
  passages?: Passage[];
}

function readSlideData(slide: SlideFields): AbsorbableSlideData {
  return (slide.slideData ?? {}) as AbsorbableSlideData;
}

/**
 * Combines a master's existing `subQuizzes` with each absorbed source's contribution, in the order
 * `orderedSources` was given — callers must pass slides in *selection* order, not list position, so the
 * result matches what the teacher saw building up (the numbered badges in SlideCard). An "other"
 * source's cloze `elements` become one SubQuiz; a reading-fluency source contributes its own
 * `subQuizzes` array directly (it may hold zero or several).
 */
export function buildMergedSubQuizzes(
  master: SlideFields,
  orderedSources: SlideFields[]
): SubQuiz[] {
  const absorbed = orderedSources.flatMap((source) =>
    isFluencyReadingSlide(source)
      ? (readSlideData(source).subQuizzes ?? [])
      : [elementsToSubQuiz((source.elements ?? []) as SlideElement[])]
  );
  return [...(readSlideData(master).subQuizzes ?? []), ...absorbed];
}

/**
 * Combines a reading-fluency master's `passages` with each absorbed reading-fluency source's
 * `passages`, in selection order (see `buildMergedSubQuizzes`). "Other" sources have no passages and
 * contribute nothing. Only call this when the master is reading-fluency — an "other" master's
 * `slideData` has no `passages` field to write into.
 */
export function buildMergedPassages(master: SlideFields, orderedSources: SlideFields[]): Passage[] {
  const absorbed = orderedSources.flatMap((source) =>
    isFluencyReadingSlide(source) ? (readSlideData(source).passages ?? []) : []
  );
  return [...(readSlideData(master).passages ?? []), ...absorbed];
}
